"use client";

import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import {
  createChart,
  createSeriesMarkers,
  CandlestickSeries,
  AreaSeries,
  HistogramSeries,
  LineSeries,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type CandlestickData,
  type AreaData,
  type HistogramData,
  type LineData,
  type SeriesType,
  type Time,
} from "lightweight-charts";

export type OhlcvBar = {
  time: Time;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
  volumeColor?: string;
};

export type AreaBar = {
  time: Time;
  value: number;
};

export type OverlayLine = {
  label: string;
  color: string;
  data: { time: Time; value: number }[];
};

/** A broker option exercise block trade — rendered as a marker above the candle it printed on */
export type ChartMarker = {
  time: Time;
  text: string;
};

export interface MarketChartHandle {
  update: (bar: OhlcvBar | AreaBar) => void;
}

interface MarketChartProps {
  type: "candlestick" | "area" | "histogram";
  data: OhlcvBar[] | AreaBar[];
  height?: number | string;
  className?: string;
  overlays?: OverlayLine[];
  markers?: ChartMarker[];
}

export const MarketChart = forwardRef<MarketChartHandle, MarketChartProps>(
  ({ type, data, height = 300, className, overlays, markers }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const seriesRef = useRef<ISeriesApi<SeriesType> | null>(null);
    const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
    const overlaySeriesRefs = useRef<ISeriesApi<"Line">[]>([]);
    const markersApiRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);

    useImperativeHandle(ref, () => ({
      update(bar: OhlcvBar | AreaBar) {
        if (!seriesRef.current) return;
        if (type === "candlestick") {
          const { volume, ...ohlc } = bar as OhlcvBar;
          (seriesRef.current as ISeriesApi<"Candlestick">).update(ohlc as CandlestickData);
          if (volume !== undefined && volumeSeriesRef.current) {
            volumeSeriesRef.current.update({ time: bar.time, value: volume } as HistogramData);
          }
        } else if (type === "area") {
          (seriesRef.current as ISeriesApi<"Area">).update(bar as AreaData);
        } else {
          (seriesRef.current as ISeriesApi<"Histogram">).update(bar as HistogramData);
        }
      },
    }));

    useEffect(() => {
      if (!containerRef.current) return;

      const isSparkline = type === "area";

      const numericHeight = typeof height === "number" ? height : containerRef.current.clientHeight || 300;

      // Canvas cannot resolve CSS variables — read computed values from the DOM
      const cs = getComputedStyle(containerRef.current);
      const c = (v: string) => cs.getPropertyValue(v).trim() || v;

      const chart = createChart(containerRef.current, {
        width: containerRef.current.clientWidth,
        height: numericHeight,
        layout: {
          background: { color: "transparent" },
          textColor: c("--mantine-color-dimmed"),
        },
        grid: {
          vertLines: { visible: !isSparkline },
          horzLines: { visible: !isSparkline },
        },
        crosshair: { mode: isSparkline ? 0 : 1 },
        rightPriceScale: { visible: !isSparkline, borderVisible: false },
        timeScale: {
          borderVisible: false,
          visible: !isSparkline,
        },
        handleScroll: !isSparkline,
        handleScale: !isSparkline,
      });

      chartRef.current = chart;

      if (type === "candlestick") {
        const candleSeries = chart.addSeries(CandlestickSeries, {
          upColor: c("--mantine-color-green-6"),
          downColor: c("--mantine-color-red-6"),
          borderVisible: false,
          wickUpColor: c("--mantine-color-green-6"),
          wickDownColor: c("--mantine-color-red-6"),
        });
        const ohlcData = (data as OhlcvBar[]).map(({ volume: _v, ...d }) => d) as CandlestickData[];
        candleSeries.setData(ohlcData);
        seriesRef.current = candleSeries;

        // Volume histogram overlay
        const volSeries = chart.addSeries(HistogramSeries, {
          color: c("--mantine-color-blue-4"),
          priceFormat: { type: "volume" },
          priceScaleId: "volume",
        });
        chart.priceScale("volume").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
        volSeries.setData(
          (data as OhlcvBar[]).map((d) => ({
            time: d.time,
            value: d.volume ?? 0,
            ...(d.volumeColor ? { color: d.volumeColor } : {}),
          })) as HistogramData[]
        );
        volumeSeriesRef.current = volSeries;
      } else if (type === "area") {
        const areaSeries = chart.addSeries(AreaSeries, {
          lineColor: c("--mantine-color-blue-6"),
          topColor: c("--mantine-color-blue-3"),
          bottomColor: "transparent",
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
        });
        areaSeries.setData(data as AreaData[]);
        seriesRef.current = areaSeries;
      } else {
        const histSeries = chart.addSeries(HistogramSeries, {
          color: c("--mantine-color-blue-6"),
        });
        histSeries.setData(data as HistogramData[]);
        seriesRef.current = histSeries;
      }

      // Overlay line series (SMA/EMA etc.)
      overlaySeriesRefs.current.forEach(s => chartRef.current?.removeSeries(s));
      overlaySeriesRefs.current = [];
      for (const overlay of overlays ?? []) {
        const lineSeries = chart.addSeries(LineSeries, {
          color: overlay.color,
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
        });
        lineSeries.setData(overlay.data as LineData[]);
        overlaySeriesRefs.current.push(lineSeries);
      }

      // Broker option exercise markers ("volume bubbles") — attached once per series instance
      if (seriesRef.current) {
        markersApiRef.current = createSeriesMarkers(
          seriesRef.current,
          (markers ?? []).map((m) => ({
            time: m.time,
            position: "aboveBar" as const,
            color: c("--mantine-color-grape-6"),
            shape: "circle" as const,
            text: m.text,
          })),
        );
      }

      // Responsive resize — track both width and height when using "100%"
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry && chartRef.current) {
          const opts: { width: number; height?: number } = { width: entry.contentRect.width };
          if (typeof height !== "number") opts.height = entry.contentRect.height;
          chartRef.current.applyOptions(opts);
          chartRef.current.timeScale().fitContent();
        }
      });
      observer.observe(containerRef.current);

      // Fit all data into view on initial render
      chart.timeScale().fitContent();

      return () => {
        observer.disconnect();
        chart.remove();
        chartRef.current = null;
        seriesRef.current = null;
        volumeSeriesRef.current = null;
        overlaySeriesRefs.current = [];
        markersApiRef.current = null;
      };
    }, [type, height, overlays?.length ?? 0]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update data when prop changes without remounting the chart
    useEffect(() => {
      if (!seriesRef.current || !data.length) return;
      if (type === "candlestick") {
        const ohlcData = (data as OhlcvBar[]).map(({ volume: _v, ...d }) => d) as CandlestickData[];
        (seriesRef.current as ISeriesApi<"Candlestick">).setData(ohlcData);
        if (volumeSeriesRef.current) {
          volumeSeriesRef.current.setData(
            (data as OhlcvBar[]).map((d) => ({ time: d.time, value: d.volume ?? 0, ...(d.volumeColor ? { color: d.volumeColor } : {}) })) as HistogramData[]
          );
        }
      } else if (type === "area") {
        (seriesRef.current as ISeriesApi<"Area">).setData(data as AreaData[]);
      } else {
        (seriesRef.current as ISeriesApi<"Histogram">).setData(data as HistogramData[]);
      }
      overlays?.forEach((o, i) => {
        overlaySeriesRefs.current[i]?.setData(o.data as LineData[]);
      });
    }, [data, type]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep markers in sync without remounting the chart
    useEffect(() => {
      if (!markersApiRef.current || !containerRef.current) return;
      // Canvas cannot resolve CSS variables — read the computed value from the DOM, same as the
      // mount effect's c() helper (falls back to the property name itself, not a hardcoded hex,
      // consistent with how every other series color in this file is resolved)
      const color = getComputedStyle(containerRef.current).getPropertyValue("--mantine-color-grape-6").trim() || "--mantine-color-grape-6";
      markersApiRef.current.setMarkers(
        (markers ?? []).map((m) => ({
          time: m.time,
          position: "aboveBar" as const,
          color,
          shape: "circle" as const,
          text: m.text,
        })),
      );
    }, [markers]);

    return <div ref={containerRef} style={{ width: "100%", height: height ?? "100%" }} className={className} />;
  }
);

MarketChart.displayName = "MarketChart";
